import {
  DocumentData,
  DocumentReference,
  getDoc,
  increment,
  serverTimestamp,
  WriteBatch,
} from "firebase/firestore";
import { getRef } from "lib/firebase";
import UUID from "uuidjs";

import { CurrentUser, NotifType } from "lib/type";

type NoitfArticleData = {
  id: string;
  title: string;
};

const likeNotif = async (
  batch: WriteBatch,
  notifRef: DocumentReference<DocumentData>,
  notifID: string,
  articleData: NoitfArticleData,
  authorUid: string,
  user: CurrentUser
) => {
  if (!user) return;

  const { id, title } = articleData;
  const { uid, name } = user;
  const articleLikedUserRef = getRef(
    "users",
    `${authorUid}/publicArticles/${id}/likedUsers/${uid}`
  );
  batch.set(articleLikedUserRef, {
    likedUid: uid,
    articleID: id,
    createdAt: serverTimestamp(),
  });
  batch.set(notifRef, {
    type: "like",
    notifID: notifID,
    articleID: id,
    userName: name,
    articleTitle: title,
    createdAt: serverTimestamp(),
  });
};

const createNotifBody = async (
  type: string,
  batch: WriteBatch,
  notifRef: DocumentReference<DocumentData>,
  notifID: string,
  articleData: NoitfArticleData,
  authorUid: string,
  user: CurrentUser
) => {
  if (!user) return;

  switch (type) {
    case "like":
      return await likeNotif(
        batch,
        notifRef,
        notifID,
        articleData,
        authorUid,
        user
      );
    case "comment":
      return batch.set(notifRef, {
        type: "comment",
        notifID: notifID,
        articleID: articleData.id,
        articleTitle: articleData.title,
        userName: user.name,
        createdAt: serverTimestamp(),
      });
    case "follow":
      return batch.set(notifRef, {
        type: "follow",
        notifID: notifID,
        notifUid: user.uid,
        userName: user.name,
        createdAt: serverTimestamp(),
      });
    default:
      return;
  }
};

export const createNotif = async (
  type: string,
  batch: WriteBatch,
  articleData: NoitfArticleData,
  user: CurrentUser,
  author: CurrentUser
) => {
  if (!user || !author) return;

  const notifID = UUID.generate();
  const notifRef = getRef("users", `${author.uid}/notifs/${notifID}`);
  await createNotifBody(
    type,
    batch,
    notifRef,
    notifID,
    articleData,
    author.uid,
    user
  );
  const authorUnreadNoitfRef = getRef(
    "users",
    `${author.uid}/unReadNotifs/${author.uid}`
  );
  const authorUnreadNoitfDoc = await getDoc(authorUnreadNoitfRef).catch(
    (err) => {
      console.log(err);
      alert("おしらせ情報の取得に失敗しました。");
      return null;
    }
  );
  if (!authorUnreadNoitfDoc) return;
  authorUnreadNoitfDoc.exists()
    ? batch.update(authorUnreadNoitfRef, {
        count: increment(1),
      })
    : batch.set(authorUnreadNoitfRef, {
        count: 1,
      });
};

export const notifTitle = (type: NotifType) => {
  switch (type) {
    case "like":
      return "いいねされました。";
    case "comment":
      return "コメントされました。";
    case "follow":
      return "フォローされました。";
    case "announce":
      return "おしらせ";
    default:
      return "おしらせはありません。";
  }
};

export const notifBody = (
  type: NotifType,
  userName: string,
  articleTitle = "",
  announce = ""
) => {
  switch (type) {
    case "like":
      return `${userName}さんが、「${articleTitle}」にいいねしました。`;
    case "comment":
      return `${userName}さんが、「${articleTitle}」にコメントしました。`;
    case "follow":
      return `${userName}さんが、あなたをフォローしました。`;
    case "announce":
      return announce;
    default:
      return "おしらせはありません。";
  }
};
